/**
 * Bundle Variant Selector Pro
 * Reusable weight × plan matrix → AJAX multi-variant add to cart
 * Compatible with Minimog cart drawer
 */
(function () {
  'use strict';

  /* --------------------------------------------------------------------------
   * Bundle Matrix — exact package configuration
   * Prices are whole currency units (e.g. 4299 = ₹4299). Converted to
   * Shopify cents/paise when formatting with formatMoney.
   * -------------------------------------------------------------------------- */
  const BUNDLE_MATRIX = {
    'up-to-11kg': {
      beginner: {
        items: [{ size: '120g', qty: 1 }],
        original: 1499,
        sale: 1499,
        savings: 0,
        dose: '½ scoop per day (4g)',
      },
      trust: {
        items: [{ size: '120g', qty: 2 }],
        original: 2998,
        sale: 2699,
        savings: 299,
        dose: '½ scoop per day (4g)',
      },
      satisfied: {
        items: [
          { size: '120g', qty: 1 },
          { size: '240g', qty: 1 },
        ],
        original: 4298,
        sale: 3199,
        savings: 1099,
        dose: '½ scoop per day (4g)',
      },
    },
    '12-26kg': {
      beginner: {
        items: [{ size: '240g', qty: 1 }],
        original: 2799,
        sale: 2499,
        savings: 300,
        dose: '1 scoop per day (8g)',
      },
      trust: {
        items: [{ size: '240g', qty: 2 }],
        original: 5598,
        sale: 4299,
        savings: 1299,
        dose: '1 scoop per day (8g)',
      },
      satisfied: {
        items: [{ size: '240g', qty: 3 }],
        original: 8397,
        sale: 5899,
        savings: 2498,
        dose: '1 scoop per day (8g)',
      },
    },
    '27-45kg': {
      beginner: {
        items: [{ size: '240g', qty: 2 }],
        original: 5598,
        sale: 3999,
        savings: 1599,
        dose: '2 scoops per day (16g)',
      },
      trust: {
        items: [{ size: '240g', qty: 4 }],
        original: 11196,
        sale: 7999,
        savings: 3197,
        dose: '2 scoops per day (16g)',
      },
      satisfied: {
        items: [{ size: '240g', qty: 6 }],
        original: 16794,
        sale: 11999,
        savings: 4795,
        dose: '2 scoops per day (16g)',
      },
    },
    '45kg-plus': {
      beginner: {
        items: [
          { size: '240g', qty: 2 },
          { size: '120g', qty: 2 },
        ],
        original: 8596,
        sale: 6399,
        savings: 2197,
        dose: '3 scoops per day (24g)',
      },
      trust: {
        items: [
          { size: '240g', qty: 4 },
          { size: '120g', qty: 4 },
        ],
        original: 17192,
        sale: 12799,
        savings: 4393,
        dose: '3 scoops per day (24g)',
      },
      satisfied: {
        items: [
          { size: '240g', qty: 8 },
          { size: '120g', qty: 2 },
        ],
        original: 25390,
        sale: 18599,
        savings: 6791,
        dose: '3 scoops per day (24g)',
      },
    },
  };

  const DEFAULT_WEIGHT = '12-26kg';
  const DEFAULT_PLAN = 'beginner';

  /**
   * Format Shopify money (already in cents/paise).
   * @param {number} cents
   * @returns {string}
   */
  function formatMoneyCents(cents) {
    const moneyFormat =
      (window.MinimogSettings && window.MinimogSettings.money_format) ||
      (window.Shopify && window.Shopify.money_format) ||
      '₹{{amount_no_decimals}}';

    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(cents, moneyFormat);
    }

    return '₹' + Math.round(Number(cents) / 100).toLocaleString('en-IN');
  }

  /**
   * Format a whole-unit price using Minimog / Shopify money helpers.
   * @param {number} amount - Price in whole currency units (not cents)
   * @returns {string}
   */
  function formatBundleMoney(amount) {
    return formatMoneyCents(Math.round(Number(amount) * 100));
  }

  /**
   * Build a human-readable includes string using live variant titles when available.
   * @param {Array<{size: string, qty: number}>} items
   * @param {{ '120g': string, '240g': string }} labels
   * @returns {string}
   */
  function formatIncludes(items, labels) {
    return items
      .map((item) => {
        let label = (labels && labels[item.size]) || item.size;
        if (typeof label === 'string') {
          label = label.replace(/249g/gi, '240g');
        }
        return item.qty + '× ' + label;
      })
      .join(' + ');
  }

  /**
   * Resolve cart line items from bundle package + variant ID map.
   * @param {object} packageConfig
   * @param {{ '120g': number|string, '240g': number|string }} variantMap
   * @returns {Array<{id: number, quantity: number}>}
   */
  function buildCartItems(packageConfig, variantMap) {
    const lines = [];

    packageConfig.items.forEach((item) => {
      const variantId = Number(variantMap[item.size]);
      if (!variantId || !item.qty) return;

      const existing = lines.find((line) => line.id === variantId);
      if (existing) {
        existing.quantity += item.qty;
      } else {
        lines.push({ id: variantId, quantity: item.qty });
      }
    });

    return lines;
  }

  /**
   * Compute compare-at total from live Shopify unit prices (cents) × qty.
   * Falls back to matrix original (rupees) when prices are missing.
   * @param {object} packageConfig
   * @param {{ '120g': number, '240g': number }} unitPricesCents
   * @returns {{ originalRupees: number, fromShopify: boolean }}
   */
  function resolveOriginalPrice(packageConfig, unitPricesCents) {
    let totalCents = 0;
    let usedShopify = true;

    packageConfig.items.forEach((item) => {
      const unit = Number(unitPricesCents[item.size] || 0);
      if (!unit) {
        usedShopify = false;
        return;
      }
      totalCents += unit * item.qty;
    });

    if (usedShopify && totalCents > 0) {
      return { originalRupees: totalCents / 100, fromShopify: true };
    }

    return { originalRupees: packageConfig.original, fromShopify: false };
  }

  class BundleVariantSelectorPro extends HTMLElement {
    constructor() {
      super();
      this.isSubmitting = false;
      this.selectedWeight = DEFAULT_WEIGHT;
      this.selectedPlan = DEFAULT_PLAN;
    }

    connectedCallback() {
      this.cacheElements();
      this.bindEvents();
      this.syncFromDom();
      this.updateUI({ animate: false });
    }

    /** Cache frequently used DOM nodes. */
    cacheElements() {
      this.els = {
        weightButtons: this.querySelectorAll('[data-bvs-weight]'),
        planCards: this.querySelectorAll('[data-bvs-plan]'),
        includes: this.querySelector('[data-bvs-includes]'),
        salePrice: this.querySelector('[data-bvs-sale]'),
        comparePrice: this.querySelector('[data-bvs-compare]'),
        savings: this.querySelector('[data-bvs-savings]'),
        savingsWrap: this.querySelector('[data-bvs-savings-wrap]'),
        dose: this.querySelector('[data-bvs-dose]'),
        priceBlock: this.querySelector('[data-bvs-price-block]'),
        atcButton: this.querySelector('[data-bvs-atc]'),
        atcLabel: this.querySelector('[data-bvs-atc-label]'),
        error: this.querySelector('[data-bvs-error]'),
        configInput: this.querySelector('[data-bvs-config]'),
        stickyBar: this.querySelector('[data-bvs-sticky]'),
        stickySale: this.querySelector('[data-bvs-sticky-sale]'),
        stickyIncludes: this.querySelector('[data-bvs-sticky-includes]'),
      };

      this.variantMap = {
        '120g': this.dataset.variant120 || '',
        '240g': this.dataset.variant240 || '',
      };

      this.unitPricesCents = {
        '120g': Number(this.dataset.price120 || 0),
        '240g': Number(this.dataset.price240 || 0),
      };

      this.sizeLabels = {
        '120g': this.dataset.label120 || '120g',
        '240g': this.dataset.label240 || '240g',
      };

      // Prefer JSON payload (more reliable than data attributes)
      const variantsNode = this.querySelector('[data-bvs-variants]');
      if (variantsNode) {
        try {
          const parsed = JSON.parse(variantsNode.textContent);
          if (parsed['120g']) this.variantMap['120g'] = String(parsed['120g']);
          if (parsed['240g']) this.variantMap['240g'] = String(parsed['240g']);
          if (parsed.prices) {
            this.unitPricesCents['120g'] = Number(parsed.prices['120g'] || 0);
            this.unitPricesCents['240g'] = Number(parsed.prices['240g'] || 0);
          }
          if (parsed.labels) {
            this.sizeLabels['120g'] = parsed.labels['120g'] || this.sizeLabels['120g'];
            this.sizeLabels['240g'] = parsed.labels['240g'] || this.sizeLabels['240g'];
          }
        } catch (error) {
          console.warn('[BundleVariantSelectorPro] Invalid variants JSON', error);
        }
      }

      this.strings = {
        addToCart: this.dataset.addToCart || 'Add to cart',
        adding: this.dataset.adding || 'Adding…',
        missingVariants:
          this.dataset.missingVariants ||
          'Bundle variants are not configured. Select the product in block settings.',
        errorGeneric: this.dataset.errorGeneric || 'Could not add this bundle. Please try again.',
        savePrefix: this.dataset.savePrefix || 'Save',
      };

      this.pricingConfig = this.readPricingConfig();
    }

    /** Read merchant discount / sale overrides from the pricing JSON node. */
    readPricingConfig() {
      const node = this.querySelector('[data-bvs-pricing]');
      if (!node) {
        return { mode: 'fixed', percent: {}, sale: {} };
      }

      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.warn('[BundleVariantSelectorPro] Invalid pricing config', error);
        return { mode: 'fixed', percent: {}, sale: {} };
      }
    }

    /**
     * Resolve the sale price for the active weight/plan using theme settings.
     * @param {string} weightKey
     * @param {string} planKey
     * @param {number} originalRupees
     * @param {number} matrixSale
     * @returns {number}
     */
    resolveSalePrice(weightKey, planKey, originalRupees, matrixSale) {
      const config = this.pricingConfig || {};
      const mode = config.mode || 'fixed';

      if (mode === 'percent') {
        const percentMap = config.percent || {};
        const percent = Number(percentMap[planKey]);
        const safePercent = Number.isFinite(percent) ? Math.min(Math.max(percent, 0), 95) : 0;
        return Math.round(originalRupees * (100 - safePercent) / 100);
      }

      const saleMap = config.sale || {};
      const weightSales = saleMap[weightKey] || {};
      const override = Number(weightSales[planKey]);
      if (Number.isFinite(override) && override >= 0) {
        return override;
      }

      return matrixSale;
    }

    /** Attach click / keyboard listeners. */
    bindEvents() {
      this.els.weightButtons.forEach((button) => {
        button.addEventListener('click', () => this.selectWeight(button.dataset.bvsWeight));
        button.addEventListener('keydown', (event) => this.handleOptionKeydown(event, 'weight'));
      });

      this.els.planCards.forEach((card) => {
        card.addEventListener('click', () => this.selectPlan(card.dataset.bvsPlan));
        card.addEventListener('keydown', (event) => this.handleOptionKeydown(event, 'plan'));
      });

      if (this.els.atcButton) {
        this.els.atcButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.addToCart();
        });
      }

      const stickyAtc = this.querySelector('[data-bvs-sticky-atc]');
      if (stickyAtc) {
        stickyAtc.addEventListener('click', (event) => {
          event.preventDefault();
          this.addToCart();
        });
      }
    }

    /** Read initially selected weight/plan from aria-pressed attributes. */
    syncFromDom() {
      const activeWeight = this.querySelector('[data-bvs-weight][aria-pressed="true"]');
      const activePlan = this.querySelector('[data-bvs-plan][aria-pressed="true"]');

      if (activeWeight) this.selectedWeight = activeWeight.dataset.bvsWeight;
      if (activePlan) this.selectedPlan = activePlan.dataset.bvsPlan;
    }

    /**
     * Arrow-key navigation within weight or plan option groups.
     * @param {KeyboardEvent} event
     * @param {'weight'|'plan'} type
     */
    handleOptionKeydown(event, type) {
      const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (!keys.includes(event.key)) return;

      event.preventDefault();

      const nodes =
        type === 'weight'
          ? Array.from(this.els.weightButtons)
          : Array.from(this.els.planCards);
      const currentIndex = nodes.indexOf(event.currentTarget);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % nodes.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + nodes.length) % nodes.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = nodes.length - 1;
      }

      nodes[nextIndex].focus();
      if (type === 'weight') {
        this.selectWeight(nodes[nextIndex].dataset.bvsWeight);
      } else {
        this.selectPlan(nodes[nextIndex].dataset.bvsPlan);
      }
    }

    /** @param {string} weightKey */
    selectWeight(weightKey) {
      if (!BUNDLE_MATRIX[weightKey] || weightKey === this.selectedWeight) return;
      this.selectedWeight = weightKey;
      this.updateUI({ animate: true });
    }

    /** @param {string} planKey */
    selectPlan(planKey) {
      if (!BUNDLE_MATRIX[this.selectedWeight][planKey] || planKey === this.selectedPlan) return;
      this.selectedPlan = planKey;
      this.updateUI({ animate: true });
    }

    /** @returns {object|null} */
    getActivePackage() {
      const weightGroup = BUNDLE_MATRIX[this.selectedWeight];
      if (!weightGroup) return null;
      return weightGroup[this.selectedPlan] || null;
    }

    /**
     * Refresh all visible UI from the active matrix entry.
     * @param {{animate?: boolean}} options
     */
    updateUI(options) {
      const animate = options && options.animate;
      const pkg = this.getActivePackage();
      if (!pkg) return;

      this.updateActiveStates();

      if (animate && this.els.priceBlock) {
        this.els.priceBlock.classList.remove('bvs-pro__price-block--fade');
        // Force reflow so the fade animation restarts
        void this.els.priceBlock.offsetWidth;
        this.els.priceBlock.classList.add('bvs-pro__price-block--fade');
      }

      if (this.els.includes) {
        this.els.includes.textContent = formatIncludes(pkg.items, this.sizeLabels);
      }

      const priced = resolveOriginalPrice(pkg, this.unitPricesCents);
      const original = priced.originalRupees;
      const sale = this.resolveSalePrice(this.selectedWeight, this.selectedPlan, original, pkg.sale);
      const savings = Math.max(0, Math.round((original - sale) * 100) / 100);

      if (this.els.salePrice) {
        this.els.salePrice.textContent = formatBundleMoney(sale);
      }

      if (this.els.comparePrice) {
        const showCompare = original > sale;
        this.els.comparePrice.textContent = formatBundleMoney(original);
        this.els.comparePrice.hidden = !showCompare;
      }

      if (this.els.savingsWrap && this.els.savings) {
        const showSavings = savings > 0;
        this.els.savingsWrap.hidden = !showSavings;
        if (showSavings) {
          this.els.savings.textContent =
            this.strings.savePrefix + ' ' + formatBundleMoney(savings);
        }
      }

      if (this.els.dose) {
        this.els.dose.textContent = pkg.dose;
      }

      if (this.els.stickySale) {
        this.els.stickySale.textContent = formatBundleMoney(sale);
      }

      if (this.els.stickyIncludes) {
        this.els.stickyIncludes.textContent = formatIncludes(pkg.items, this.sizeLabels);
      }

      this.writeHiddenConfig(pkg, { original: original, sale: sale, savings: savings });
      this.clearError();
    }

    /** Toggle aria-pressed / active classes on weight + plan controls. */
    updateActiveStates() {
      this.els.weightButtons.forEach((button) => {
        const isActive = button.dataset.bvsWeight === this.selectedWeight;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.els.planCards.forEach((card) => {
        const isActive = card.dataset.bvsPlan === this.selectedPlan;
        card.classList.toggle('is-active', isActive);
        card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    /**
     * Persist current selection for debugging / optional form hooks.
     * @param {object} pkg
     * @param {{original?: number, sale?: number, savings?: number}} [priced]
     */
    writeHiddenConfig(pkg, priced) {
      if (!this.els.configInput) return;

      const payload = {
        weight: this.selectedWeight,
        plan: this.selectedPlan,
        items: pkg.items,
        original: priced && priced.original != null ? priced.original : pkg.original,
        sale: priced && priced.sale != null ? priced.sale : pkg.sale,
        savings: priced && priced.savings != null ? priced.savings : pkg.savings,
        dose: pkg.dose,
        labels: this.sizeLabels,
        unitPricesCents: this.unitPricesCents,
      };

      this.els.configInput.value = JSON.stringify(payload);
    }

    /** Validate that required variant IDs exist for the active package. */
    variantsConfigured(pkg) {
      if (!pkg || !pkg.items) return false;

      return pkg.items.every((item) => Boolean(Number(this.variantMap[item.size])));
    }

    /**
     * POST multi-item payload to /cart/add.js and open Minimog drawer.
     */
    async addToCart() {
      if (this.isSubmitting) return;

      const pkg = this.getActivePackage();
      if (!pkg) return;

      if (!this.variantsConfigured(pkg)) {
        this.showError(this.strings.missingVariants);
        return;
      }

      const items = buildCartItems(pkg, this.variantMap);
      if (!items.length) {
        this.showError(this.strings.missingVariants);
        return;
      }

      // Match Minimog product-bundles: string IDs in JSON body
      const normalizedItems = items.map((item) => ({
        id: String(item.id),
        quantity: Number(item.quantity) || 1,
      }));

      if (normalizedItems.some((item) => !item.id || item.id === '0' || !item.quantity)) {
        this.showError(this.strings.missingVariants);
        return;
      }

      this.setLoading(true);
      this.clearError();

      try {
        const data = await this.postCartItems(normalizedItems);

        if (data && (data.status || data.message === 'Cart Error')) {
          const message =
            data.description || data.message || this.strings.errorGeneric;
          this.showError(message);
          document.dispatchEvent(
            new CustomEvent('product-ajax:error', {
              detail: { errorMessage: message },
            })
          );
          return;
        }

        try {
          await this.openMinimogCart(data);
        } catch (drawerError) {
          console.error('[BundleVariantSelectorPro] Cart drawer refresh failed', drawerError);
          document.dispatchEvent(
            new CustomEvent('cart:refresh', {
              bubbles: true,
              detail: { open: true },
            })
          );
        }
      } catch (error) {
        console.error('[BundleVariantSelectorPro]', error);
        this.showError(
          (error && error.message) || this.strings.errorGeneric
        );
      } finally {
        this.setLoading(false);
      }
    }

    /**
     * POST items to Minimog cart_add_url (same pattern as product-bundles.js).
     * @param {Array<{id: string, quantity: number}>} items
     * @returns {Promise<object>}
     */
    async postCartItems(items) {
      const root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      let cartAddUrl =
        (window.MinimogSettings &&
          window.MinimogSettings.routes &&
          window.MinimogSettings.routes.cart_add_url) ||
        root + 'cart/add';

      // Prefer the Ajax JSON endpoint
      if (cartAddUrl.indexOf('.js') === -1) {
        cartAddUrl = cartAddUrl.replace(/\/?(\?|$)/, '.js$1');
      }

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      sectionsToBundle = Array.from(new Set(sectionsToBundle.filter(Boolean)));

      const pathname = window.location.pathname || '/';
      const sectionsUrl = pathname.charAt(0) === '/' ? pathname : '/' + pathname;

      let itemCountBefore = 0;
      try {
        const beforeRes = await fetch(root + 'cart.js', {
          headers: { Accept: 'application/json' },
        });
        const beforeCart = await beforeRes.json();
        itemCountBefore = (beforeCart && beforeCart.item_count) || 0;
      } catch (beforeError) {
        // Ignore — only used to detect false 400s
      }

      const post = async (body) => {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        const raw = await response.text();
        let data = null;
        try {
          data = raw ? JSON.parse(raw) : {};
        } catch (parseError) {
          console.error(
            '[BundleVariantSelectorPro] Non-JSON cart response',
            response.status,
            raw
          );
          throw new Error(this.strings.errorGeneric);
        }
        return { response: response, data: data };
      };

      // Match Minimog product-bundles payload
      let result = await post({
        items: items,
        sections: sectionsToBundle,
        sections_url: sectionsUrl,
      });

      // Invalid sections/sections_url can return HTTP 400 even when items were added.
      if (result.response.status === 400) {
        try {
          const afterRes = await fetch(root + 'cart.js', {
            headers: { Accept: 'application/json' },
          });
          const afterCart = await afterRes.json();
          if (afterCart && afterCart.item_count > itemCountBefore) {
            console.warn(
              '[BundleVariantSelectorPro] HTTP 400 after add, but cart grew — treating as success'
            );
            return afterCart;
          }
        } catch (afterError) {
          console.warn('[BundleVariantSelectorPro] Cart verify failed', afterError);
        }

        // Retry once without sections
        result = await post({ items: items });
      }

      return result.data;
    }

    /**
     * Refresh / open Minimog cart drawer after a successful add.
     * @param {object} response - /cart/add.js JSON response
     */
    async openMinimogCart(response) {
      const cartDrawer = document.querySelector('m-cart-drawer');
      const settings = window.MinimogSettings || {};
      const strings = window.MinimogStrings || {};

      if (settings.use_ajax_atc === false && settings.routes && settings.routes.cart) {
        window.location = settings.routes.cart;
        return;
      }

      // Always refresh via Section Rendering API — works even when add response
      // has no `sections` HTML (Minimog cartUpdate handlers require sections).
      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { open: true },
        })
      );

      if (cartDrawer && settings.enable_cart_drawer !== false) {
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }

        try {
          if (
            window.MinimogTheme &&
            MinimogTheme.Notification &&
            cartDrawer.querySelector('m-cart-items')
          ) {
            MinimogTheme.Notification.show({
              target: cartDrawer.querySelector('m-cart-items'),
              method: 'prepend',
              type: 'success',
              message: strings.itemAdded || 'Item added to cart',
              delay: 400,
            });
          }
        } catch (notifyError) {
          console.warn('[BundleVariantSelectorPro] Notification failed', notifyError);
        }

        if (cartDrawer.classList.contains('m-cart--empty')) {
          cartDrawer.classList.remove('m-cart--empty');
        }

        const drawerItems = cartDrawer.querySelector('m-cart-drawer-items');
        if (drawerItems && drawerItems.classList.contains('m-cart--empty')) {
          drawerItems.classList.remove('m-cart--empty');
        }
      } else if (window.MinimogTheme && MinimogTheme.Notification) {
        MinimogTheme.Notification.show({
          target: this.els.error ? this.els.error.parentElement : document.body,
          method: 'appendChild',
          type: 'success',
          message: strings.itemAdded || 'Item added to cart',
          last: 3000,
        });
      }

      // Only emit Minimog cartUpdate when sections HTML is present — otherwise
      // m-cart-items / m-cart-count throw on event.cart.sections[...]
      if (
        response &&
        response.sections &&
        window.MinimogEvents &&
        window.MinimogTheme &&
        MinimogTheme.pubSubEvents
      ) {
        window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, {
          cart: response,
        });
      }

      document.dispatchEvent(
        new CustomEvent('product-ajax:added', {
          detail: { product: response },
        })
      );
    }

    /** @param {boolean} loading */
    setLoading(loading) {
      this.isSubmitting = loading;
      const buttons = this.querySelectorAll('[data-bvs-atc], [data-bvs-sticky-atc]');

      buttons.forEach((button) => {
        button.disabled = loading;
        button.setAttribute('aria-busy', loading ? 'true' : 'false');
        button.classList.toggle('is-loading', loading);
        button.classList.toggle('m-spinner-loading', loading);
      });

      if (this.els.atcLabel) {
        this.els.atcLabel.textContent = loading ? this.strings.adding : this.strings.addToCart;
      }

      const stickyLabel = this.querySelector('[data-bvs-sticky-atc-label]');
      if (stickyLabel) {
        stickyLabel.textContent = loading ? this.strings.adding : this.strings.addToCart;
      }
    }

    /** @param {string} message */
    showError(message) {
      if (!this.els.error) return;
      this.els.error.hidden = false;
      this.els.error.textContent = message;
      this.els.error.setAttribute('role', 'alert');
    }

    clearError() {
      if (!this.els.error) return;
      this.els.error.hidden = true;
      this.els.error.textContent = '';
    }
  }

  // Expose matrix for debugging / extension
  window.BundleVariantSelectorProMatrix = BUNDLE_MATRIX;

  if (!customElements.get('bundle-variant-selector-pro')) {
    customElements.define('bundle-variant-selector-pro', BundleVariantSelectorPro);
  }
})();
